import { CAMPAIGN_DAILY_LIMIT_MAX, CAMPAIGN_DAILY_LIMIT_MIN, isValidCampaignDailyLimit } from "./campaignDailyLimit";
import { Autocomplete, Box, Button, Chip, CircularProgress, Stack, TextField, Typography, useTheme, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { ArrowBack, Close, NavigateNext, OpenInNew, Refresh } from "@mui/icons-material";
import { ChangeEvent, useEffect, useState } from "react";
import { urls } from "@routes";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import { MAX_CAMPAIGN_CONTACT_LISTS } from "@lib/utils";
import { useSnackbar } from "notistack";
import CampaignDomainSelection, { CDSManagedSubdomain } from "./components/CampaignDomainSelection";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import CampaignSkipDaysSelection from "@components/CampaignSkipDaysSelection";
import { CustomCellRendererProps } from "ag-grid-react";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { format } from "date-fns";
import ServerSideDataTable from "@components/ServerSideDataTable";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DateTime } from "luxon";

interface ContactList {
    uid: string
    name: string
    created_on: number
    total_contacts: number
    total_columns: number
    email_verification_status: string | null
}

const VERIFICATION_IN_FLIGHT_STATUSES = ["queued", "processing", "skipping"];

function isVerificationInFlight(status: string | null | undefined): boolean {
    return VERIFICATION_IN_FLIGHT_STATUSES.includes(status ?? "");
}

function VerificationStatusCell({ data }: CustomCellRendererProps) {
    const status: string | null = data?.email_verification_status ?? null;

    if (isVerificationInFlight(status)) {
        return <Chip size="small" color="info" variant="outlined"
            icon={<CircularProgress size={12} />} label="Verifying" />;
    }
    if (status === "completed") {
        return <Chip size="small" color="success" variant="outlined" label="Verified" />;
    }
    if (status === "skipped") {
        return <Chip size="small" color="warning" variant="outlined" label="Partially Verified" />;
    }
    if (status === "failed") {
        return <Chip size="small" color="error" variant="outlined" label="Failed" />;
    }
    return <Chip size="small" variant="outlined" label="Not Verified" />;
}

interface PageData {
    status_code: string
    status_text: string

    campaigns_blocked: boolean
    managed_subdomains: CDSManagedSubdomain[]
    contact_lists: ContactList[]
}

interface ContactListQueryResult {
    status_code: string
    status_text: string

    contact_lists: ContactList[]
}

const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];


export default function AdminCreateCampaigns() {
    const { userId, workspaceId } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();

    const [pageData, setPageData] = useState<PageData>();
    const [contactLists, setContactLists] = useState<ContactList[]>([]);
    const [campaignName, setCampaignName] = useState<string>("");
    const [selectedContactLists, setSelectedContactLists] = useState<ContactList[]>([]);
    const [emailsPerDay, setEmailsPerDay] = useState<string>("50");
    const [replyToAddress, setReplyToAddress] = useState<string>("");
    const [
        selectedDomains,
        setSelectedDomains
    ] = useState<CDSManagedSubdomain[]>([]);
    const [skipDays, setSkipDays] = useState<Array<string>>(days);
    const [fromTime, setFromTime] = useState("");
    const [toTime, setToTime] = useState("");
    const [selectedTimezone, setSelectedTimezone] = useState("");

    const [proceedButtonDisabled, setProceedButtonDisabled] = useState<boolean>(true);
    const timeZones = (Intl as any).supportedValuesOf
        ? (Intl as any).supportedValuesOf("timeZone")
        : [];


    // Fetch page data.
    const pageDataQuery = useQuery({
        queryKey: ["createCampaignPageData"],
        queryFn: () => authenticateAndFetchData(`/admin/create-campaign/?workspace_id=${workspaceId}&user_id=${userId}`),
        gcTime: 0,
        retry: retryFn,
        refetchOnWindowFocus: false,
    });
    useEffect(() => {
        if (pageDataQuery.data) {
            let data = pageDataQuery.data.data as PageData
            setPageData(data);
            setContactLists(data.contact_lists);
        }
    }, [pageDataQuery.data]);

    // Query to fetch just the contact list data. Won't fire on page load.
    const contactListsQuery = useQuery({
        queryKey: ["contactListsQuery"],
        queryFn: () => authenticateAndFetchData(`/admin/campaigns-create/fetch-contact-lists/?workspace_id=${workspaceId}`),
        gcTime: 0,
        retry: retryFn,
        enabled: false,
        refetchOnWindowFocus: false,
    });
    useEffect(() => {
        if (contactListsQuery.data) {
            let data = contactListsQuery.data.data as ContactListQueryResult
            setContactLists(data.contact_lists);
        }
    }, [contactListsQuery.data]);

    const tooManyContactLists = selectedContactLists.length > MAX_CAMPAIGN_CONTACT_LISTS;

    // Unlocks proceed button if condition is satisfied.
    useEffect(() => {
        if (campaignName && (selectedContactLists.length > 0) &&
            selectedContactLists.length <= MAX_CAMPAIGN_CONTACT_LISTS && isValidCampaignDailyLimit(emailsPerDay)) {
            setProceedButtonDisabled(false);
        } else {
            setProceedButtonDisabled(true);
        }
    }, [campaignName, selectedContactLists, emailsPerDay]);

    // Mutation to create campaign.
    const createCampaignMutation = useMutation({
        mutationKey: ["createCampaignMutation"],
        mutationFn: () => authenticateAndPostData("/admin/create-campaign/", {
            "name": campaignName,
            "emails_per_day": Number(emailsPerDay),
            "reply_to_address": replyToAddress,
            "selected_contact_list_uids": selectedContactLists.map(value => value.uid),
            "selected_domain_ids": selectedDomains
                .filter(value => value.checked)
                .map(value => value.id),
            "skip_days": days.filter(value => !skipDays.includes(value)),
            "from_time": fromTime,
            "to_time": toTime,
            "time_zone": selectedTimezone,
            "workspace_id": workspaceId,
            "user_id": userId,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            navigate(urls["adminUserCampaigns"].replace(":userId", userId || ""));
        },
        onError: (error: ApiRequestFailed) => {
            console.log(error);
            enqueueSnackbar(
                error.data.message || "Something went wrong on our side. Please try again in some time.",
                {
                    variant: "error",
                });
        }
    });

    const parseTime = (timeString: string | null) =>
        timeString
            ? DateTime.fromFormat(timeString, "HH:mm:ss").set({
                year: 2025,
                month: 1,
                day: 1,
            }) // dummy date
            : null;

    // convert Luxon DateTime → "HH:mm:ss" for API
    const formatTime = (dt: DateTime | null) =>
        dt ? dt.toFormat("HH:mm:ss") : "";

    function emailsPerDayHandler(event: ChangeEvent<HTMLInputElement>) {
        // Only allow numbers.
        const newValue = event.target.value;
        if (newValue === '' || /^[0-9\b]+$/.test(newValue)) {
            setEmailsPerDay(newValue);
        }
    }

    function submitHandler() {
        if (!isValidCampaignDailyLimit(emailsPerDay)) return;
        // if (selectedDomains.length === 0) {
        // 	enqueueSnackbar("Please select at least one domain for sending emails in this campaign.", {
        // 		variant: "error",
        // 	});
        // 	return;
        // }

        if (tooManyContactLists) {
            enqueueSnackbar(
                `A campaign can target at most ${MAX_CAMPAIGN_CONTACT_LISTS} contact lists.`,
                { variant: "error" }
            );
            return;
        }

        if (campaignName && (selectedContactLists.length > 0)) {
            createCampaignMutation.mutate();

        } else {
            enqueueSnackbar("Please check and fill in all the details and select at least one contact list to use.",
                {
                    variant: "error",
                }
            );
        }
    }

    // --------------------- TABLE SETUP ---------------------

    function timestampFormatter(params: ValueFormatterParams) {
        return format(new Date(params.value), "do MMM y, HH:mm:ss (xxx)");
    }

    const ContactsPreviewButton = (customProps: CustomCellRendererProps) => {
        return (
            <Button component={Link}
                to={urls["contactListDetails"].replace(":contactListUID", customProps.data["uid"])}
                target={"_blank"}
                rel={"noreferrer"}
                size={"small"}
                startIcon={<OpenInNew />}>
                Preview
            </Button>
        )
    }

    // Columns for contact lists table.
    const contactListSelectionTableColumnDefs: ColDef[] = [
        { field: "name", headerName: "Name" },
        { field: "created_on", headerName: "Imported On", valueFormatter: timestampFormatter, flex: 2 },
        { field: "total_contacts", headerName: "Total Contacts" },
        { field: "total_columns", headerName: "Total Columns" },
        { field: "email_verification_status", headerName: "Verification Status", cellRenderer: VerificationStatusCell },
        { cellRenderer: ContactsPreviewButton, resizable: false, sortable: false }
    ]

    // Table action buttons.
    const ContactListTableActions = () => (
        <Stack direction={"row"} spacing={2}>
            <Button startIcon={<Refresh />}
                variant={"contained"}
                size={"small"}
                disabled={contactListsQuery.isFetching || pageDataQuery.isFetching}
                onClick={() => contactListsQuery.refetch().then()}>
                {contactListsQuery.isFetching ? "Loading..." : "Refresh"}
            </Button>
            <Button component={Link}
                to={urls["adminUserContactList"].replace(":userId", userId || "")}
                target={"_blank"}
                rel={"noreferrer"}
                startIcon={<OpenInNew />}
                variant={"contained"}
                size={"small"}>
                Add New Contact List
            </Button>
        </Stack>
    )

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    if (pageDataQuery.isLoading) {
        return (
            <PageLoading />
        )

    } else if (pageDataQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={pageDataQuery.error as unknown as ApiRequestFailed} />

    } else if (pageData) {
        if (pageData.campaigns_blocked) {
            return (
                <Stack justifyContent={"center"} alignItems={"center"}>
                    <Typography variant={"h4"} color={"primary"} align={"center"}>Account Blocked</Typography>
                    <Typography variant={"body1"} align={"center"} sx={{ mt: 1 }}>
                        Your ability to create new email campaigns has been temporarily suspended.<br />
                        Please contact support for any further assistance.
                    </Typography>
                    <Button variant={"contained"}
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(-1)}
                        sx={{ mt: 2 }}>
                        Go Back
                    </Button>
                </Stack>
            )

        } else {
            return (
                <Stack direction={"column"} spacing={4} sx={{ pb: 6 }}>
                    <Stack direction={"column"} spacing={1}>
                        <Typography variant={"h4"} color={"primary"}>Create New Campaign</Typography>
                        <Typography variant={"subtitle1"}>
                            Upload contacts, craft your email, choose your sending domains. We automate everything else for you.
                        </Typography>
                    </Stack>

                    {/* Campaign name */}
                    <Stack direction={"column"} spacing={1}>
                        <Typography variant={"subtitle1"} sx={{ fontWeight: "bold" }}>
                            <span style={{ color: theme.palette.error.main }}>*</span>&nbsp;Give a name for your Campaign:
                        </Typography>
                        <TextField value={campaignName}
                            onChange={e => setCampaignName(e.target.value)}
                            label="Campaign Name"
                            variant="outlined"
                            sx={{ maxWidth: "500px" }}
                            required />
                    </Stack>

                    {/* Contact list selection table */}
                    <Stack direction={"column"} spacing={1}>
                        <Typography variant={"subtitle1"} sx={{ fontWeight: "bold" }}>
                            <span style={{ color: theme.palette.error.main }}>*</span>&nbsp;Select one or more contact lists to use
                            &nbsp;({selectedContactLists.length} / {MAX_CAMPAIGN_CONTACT_LISTS} selected):
                        </Typography>
                        {tooManyContactLists && (
                            <Typography variant={"body2"} sx={{ color: theme.palette.error.main }}>
                                A campaign can target at most {MAX_CAMPAIGN_CONTACT_LISTS} contact lists.
                                Deselect {selectedContactLists.length - MAX_CAMPAIGN_CONTACT_LISTS} to continue.
                            </Typography>
                        )}
                        <Box sx={{ height: "500px" }}>
                            <ServerSideDataTable columns={contactListSelectionTableColumnDefs}
                                rows={contactLists}
                                onSelectionChange={(rows) => {
                                    setSelectedContactLists(rows);
                                }}
                                rowSelection={{
                                    mode: "multiRow",
                                    headerCheckbox: false,
                                    isRowSelectable: (node) => !isVerificationInFlight(node.data?.email_verification_status),
                                }}
                                actions={ContactListTableActions()} />
                        </Box>
                    </Stack>

                    {/* Emails Per Day */}
                    <Stack direction={"column"} spacing={1}>
                        <Box>
                            <Typography variant={"subtitle1"} fontWeight={"bold"}>
                                Daily sending limit per domain for this campaign
                            </Typography>
                            <Typography variant={"body2"}>
                                Each selected domain can send up to 100 emails daily for this campaign. Domains can be shared across campaigns.
                            </Typography>
                            {/*<Typography variant={"body2"} sx={{mt: 2}}>*/}
                            {/*	(NOTE: Less than provided number of emails might be sent on a day depending on certain factors)*/}
                            {/*</Typography>*/}
                        </Box>
                        <TextField
                            type="text"
                            value={emailsPerDay}
                            onChange={emailsPerDayHandler}
                            variant="outlined"
                            placeholder="50"
                            error={!isValidCampaignDailyLimit(emailsPerDay)}
                            helperText={!isValidCampaignDailyLimit(emailsPerDay) ? "Enter a whole number between 1 and 100." : undefined}
                            slotProps={{
                                htmlInput: {
                                    'aria-label': 'Daily sending limit per domain',
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                    min: CAMPAIGN_DAILY_LIMIT_MIN,
                                    max: CAMPAIGN_DAILY_LIMIT_MAX
                                }
                            }}
                            sx={{ maxWidth: "100px" }}
                        />
                    </Stack>

                    {/* Reply-To */}
                    <Stack direction={"column"} spacing={1}>
                        <Box>
                            <Typography variant={"subtitle1"} fontWeight={"bold"}>
                                Where do you want to receive all replies on?
                            </Typography>
                            <Typography variant={"body2"}>
                                This email will be used as the destination mailbox for all replies.
                                Leave empty to use your account email.
                            </Typography>
                        </Box>
                        <TextField
                            type="text"
                            value={replyToAddress}
                            onChange={e => setReplyToAddress(e.target.value)}
                            label={"john@example.com"}
                            variant="outlined"
                            sx={{ maxWidth: "500px" }}
                        />
                    </Stack>

                    {/* Managed subdomain selection */}
                    <CampaignDomainSelection items={pageData.managed_subdomains}
                        onChange={(value: CDSManagedSubdomain[]) => {
                            setSelectedDomains(value);
                        }} />

                    {/* Campaign Skip Days Selection */}
                    <CampaignSkipDaysSelection skipDays={skipDays} onChange={value => setSkipDays(value)} />

                    {/* ------------------------ From / To Time ------------------------ */}
                    <Stack direction={"column"} spacing={3}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Set timezone for campaign email sending:
                            </Typography>
                            <Autocomplete
                                options={[
                                    // Add UTC manually at the top
                                    {
                                        label: "UTC (+00:00)",
                                        value: "UTC",
                                    },
                                    ...timeZones.map((tz) => {
                                        const now = new Date();
                                        const offsetFormatter = new Intl.DateTimeFormat("en-US", {
                                            timeZone: tz,
                                            timeZoneName: "shortOffset",
                                        });
                                        const offsetParts = offsetFormatter.formatToParts(now);
                                        const rawOffset = offsetParts
                                            .find((p) => p.type === "timeZoneName")
                                            ?.value.replace("GMT", "");

                                        return {
                                            label: `${tz} (${rawOffset} UTC)`,
                                            value: tz,
                                        };
                                    }),
                                ]}
                                value={
                                    selectedTimezone
                                        ? {
                                            label:
                                                selectedTimezone === "UTC"
                                                    ? "UTC (+00:00)"
                                                    : `${selectedTimezone} (${new Intl.DateTimeFormat("en-US", {
                                                        timeZone: selectedTimezone,
                                                        timeZoneName: "shortOffset",
                                                    })
                                                        .formatToParts(new Date())
                                                        .find((p) => p.type === "timeZoneName")
                                                        ?.value.replace("GMT", "")} UTC)`,
                                            value: selectedTimezone,
                                        }
                                        : null
                                }
                                onChange={(_, newValue) => {
                                    if (newValue) {
                                        setSelectedTimezone(newValue.value);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Timezone" sx={{ maxWidth: 400 }} />
                                )}
                                getOptionLabel={(option) => option.label}
                            />
                        </Box>
                    </Stack>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <Stack direction="column" spacing={3}>
                            <Stack direction="column">
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                    Select time range to send emails:
                                </Typography>
                                <Typography variant="body2">
                                    Choose a start and end time (must be at least 3 hours apart).
                                </Typography>
                            </Stack>

                            <Stack direction="row" spacing={2}>
                                <TimePicker
                                    ampm
                                    views={['hours']}
                                    format="hh:mm a"
                                    label="From Time"
                                    value={parseTime(fromTime)}
                                    onChange={(val) => {
                                        if (!val) return;
                                        const formatted = formatTime(val);
                                        setFromTime(formatted);

                                        // Auto bump ToTime if invalid
                                        const minTo = val.plus({ hours: 3 });
                                        const toVal = parseTime(toTime);
                                        if (!toVal || toVal < minTo) {
                                            const adjusted = formatTime(minTo);
                                            setToTime(adjusted);
                                        }
                                    }}
                                />

                                <TimePicker
                                    ampm
                                    label="To Time"
                                    format="hh:mm a"
                                    views={['hours']}
                                    value={parseTime(toTime)}
                                    onChange={(val) => {
                                        if (!val) return;

                                        const formatted = formatTime(val);
                                        setToTime(formatted);
                                        const from = parseTime(fromTime);
                                        const to = val;

                                        if (from && to) {
                                            let diff = to.diff(from, "minutes").minutes ?? 0;
                                            if (diff < 0) diff += 24 * 60;

                                            if (diff >= 180) {
                                                // only save if valid								
                                                setToTime(formatted);
                                            }
                                        }
                                    }}
                                    minTime={fromTime ? parseTime(fromTime)?.plus({ hours: 3 }) ?? undefined : undefined}
                                    slotProps={{
                                        textField: {
                                            error: (() => {
                                                if (!fromTime || !toTime) return false;
                                                const from = parseTime(fromTime);
                                                const to = parseTime(toTime);
                                                if (!from || !to) return false;

                                                let diff = to.diff(from, "minutes").minutes ?? 0;
                                                if (diff < 0) diff += 24 * 60;
                                                return diff < 180;
                                            })(),
                                            helperText: (() => {
                                                if (!fromTime || !toTime) return "";
                                                const from = parseTime(fromTime);
                                                const to = parseTime(toTime);
                                                if (!from || !to) return "";
                                                let diff = to.diff(from, "minutes").minutes ?? 0;
                                                if (diff < 0) diff += 24 * 60;
                                                return diff < 180 ? "End time must be at least 3 hours after start time" : "";
                                            })(),
                                        },
                                    }}
                                />
                            </Stack>
                        </Stack>
                    </LocalizationProvider>

                    {/* Actions */}
                    <Stack direction={"row"} spacing={4}>
                        <Button variant={"outlined"} startIcon={<Close />}
                            onClick={() => {
                                navigate(urls["campaigns"]);
                            }}>
                            Cancel
                        </Button>
                        <Button variant={"contained"}
                            endIcon={createCampaignMutation.isPending ? <CircularProgress size={20} color="inherit" /> :
                                <NavigateNext />}
                            disabled={proceedButtonDisabled || createCampaignMutation.isPending}
                            onClick={submitHandler}>
                            {createCampaignMutation.isPending ? "Uploading Data..." : "Proceed"}
                        </Button>
                    </Stack>
                </Stack>
            );
        }
    } else {
        // Ideally it should not reach here.
        return <></>
    }
}
